import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Package, Check } from 'lucide-react';
import { products } from '../../data/products';

const InventoryTab = ({ password }) => {
  const [stock, setStock] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);
  const [savedFlash, setSavedFlash] = useState(null);

  const fetchStock = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stock-status`);
      if (res.ok) setStock(await res.json());
    } catch { /* retry on refresh */ }
  }, []);

  useEffect(() => { fetchStock(); }, [fetchStock]);

  const saveStock = async (name) => {
    const value = parseInt(edits[name]);
    if (isNaN(value) || value < 0) return;
    setBusy(name);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stock/${encodeURIComponent(name)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, remaining: value })
      });
      if (res.ok) {
        await fetchStock();
        setEdits(prev => { const n = { ...prev }; delete n[name]; return n; });
        setSavedFlash(name);
        setTimeout(() => setSavedFlash(null), 2000);
      }
    } finally {
      setBusy(null);
    }
  };

  const rows = products.map(p => {
    const info = stock?.[p.name];
    return { name: p.name, image: p.image, remaining: info?.remaining ?? null, inStock: info?.in_stock ?? true };
  });

  return (
    <div data-testid="inventory-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Inventory</h1>
          <p className="text-sm text-foreground/60 mt-1">Stock automatically reduces when an order is placed. Adjust counts below after restocking.</p>
        </div>
        <button onClick={fetchStock} className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors" data-testid="refresh-inventory-button">
          <RefreshCw className="w-4 h-4" /><span className="text-sm">Refresh</span>
        </button>
      </div>

      {!stock ? (
        <div className="text-center py-16">
          <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4 animate-pulse" />
          <p className="text-foreground/60">Loading stock...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(row => {
            const soldOut = row.remaining === 0;
            const unlimited = row.remaining === -1 || row.remaining === null;
            return (
              <div key={row.name} className="p-5 rounded-2xl border border-border bg-background/50 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`inventory-row-${row.name.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center space-x-4">
                  {row.image && (
                    <div className="w-16 h-16 rounded-xl bg-muted/40 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img src={row.image} alt={row.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{row.name}</h3>
                    <span className={`inline-block mt-1 px-3 py-0.5 rounded-full text-xs font-medium ${soldOut ? 'bg-red-100 text-red-700' : unlimited ? 'bg-blue-100 text-blue-700' : row.remaining <= 10 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`} data-testid={`stock-badge-${row.name.toLowerCase().replace(/\s/g, '-')}`}>
                      {soldOut ? 'Sold Out' : unlimited ? 'Unlimited' : row.remaining <= 10 ? `Low — ${row.remaining} left` : `${row.remaining} in stock`}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right mr-2">
                    <p className="text-xs text-foreground/50">Current</p>
                    <p className="text-2xl font-bold text-foreground" data-testid={`stock-count-${row.name.toLowerCase().replace(/\s/g, '-')}`}>{unlimited ? '∞' : row.remaining}</p>
                  </div>
                  <input
                    type="number" min="0"
                    placeholder="Set stock"
                    value={edits[row.name] ?? ''}
                    onChange={e => setEdits(prev => ({ ...prev, [row.name]: e.target.value }))}
                    className="w-28 px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    data-testid={`stock-input-${row.name.toLowerCase().replace(/\s/g, '-')}`}
                  />
                  <button
                    onClick={() => saveStock(row.name)}
                    disabled={busy === row.name || edits[row.name] === undefined || edits[row.name] === ''}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center space-x-1.5"
                    data-testid={`stock-save-${row.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {savedFlash === row.name ? <><Check className="w-4 h-4" /><span>Saved</span></> : <span>{busy === row.name ? '...' : 'Update'}</span>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InventoryTab;
